import angular from 'angular'
import { IndexNames, StoreNames } from 'idb'
import filter from 'lodash/filter'
import map from 'lodash/map'

import { WebRegisterDb } from '../database/database-types'
import { syncManager } from '../../ng-compat/managers'
import { store } from '../database/database'
import { AutocompleteSource } from './autocomplete-source'

export const ExactMatchIndex = ExactMatchIndexFactory()

/**
 * A search index for searching for entities by exact match on a particular property.
 *
 * @module ExactMatchIndex
 */
angular
  .module('webregister.factories')
  .factory('ExactMatchIndex', () => ExactMatchIndex)

function ExactMatchIndexFactory() {
  return {
    /**
     * Create an a new AutocompleteSource for exact match querying of the specified entity.
     *
     * @method create
     *
     * @param {Object} options
     *
     *        @param {String} options.name
     *               A unique name to identify this search index in log messages etc
     *
     *        @param {String} options.entityName
     *               The name of the entity being searched e.g. 'product'
     *
     *        @param {String} options.entityStoreName
     *               The name of the database store for the entity e.g. 'products'
     *
     *        @param {String} options.databaseIndexName
     *               The name of the database index to search e.g. 'bySku'
     *
     *        @param {Function} [options.filterResultsBy]
     *               Optional function to filter results by - see lodash.filter documentation for details
     *
     * @return {AutocompleteSource} an AutocompleteSource for exact match querying of the specified entity
     */
    create<
      StoreName extends StoreNames<WebRegisterDb>,
      IndexName extends IndexNames<WebRegisterDb, StoreName>
    >({
      name,
      entityName,
      entityStoreName,
      databaseIndexName,
      filterResultsBy,
    }: {
      name: string
      entityName: string
      entityStoreName: StoreName
      databaseIndexName: IndexName
      filterResultsBy?: $TSFixMeFunction
    }) {
      const dbIndex = store(entityStoreName).index(databaseIndexName)

      return new AutocompleteSource({
        name,
        types: [entityName],
        whenSyncComplete() {
          return syncManager.whenCompleteEntityDataAvailable(entityStoreName)
        },
        search(query) {
          return (
            dbIndex
              .getAll(query as $TSFixMe)
              // If filterResultsBy is not defined, lodash will use the identity function (i.e. let every result past)
              .then(results => filter(results, filterResultsBy))
              .then(results =>
                map(results, result => {
                  return {
                    id: (result as $TSFixMe).id,
                    type: entityName,
                    exactMatch: true,
                    [entityName]: result,
                  }
                })
              )
          )
        },
      })
    },
  }
}
